import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validateRecentAccess } from '../services/api';

// ─── Path → label mapping ────────────────────────────────────
const PATH_LABELS = {
  overview: 'Outcomes',
  agents: 'Agent Lifecycle',
  canvas: 'Workflow Canvas',
  approvals: 'Approval Center',
  deployments: 'Organizations',
  connectors: 'Connectors',
  'agent-marketplace': 'Agents',
  team: 'Team',
  intelligence: 'Intelligence Center',
  templates: 'Template Center',
  // Explicit label so the "Back to" pill renders "Cloud Operations"
  // deterministically (instead of humanising the segment to "Cloud Operations"
  // by chance). Matches the user-facing nav entry + DeployHomePage title.
  'cloud-operations': 'Cloud Operations',
};

const STORAGE_KEY = 'agentlets_nav_trail';
const CONTEXT_STORAGE_KEY = 'agentlets_nav_context';
// Precise "recent entities" history — the specific Agent / Workflow / Skill /
// Connector (etc.) the user was working on, most-recent-first, capped at the
// last 7 distinct items. Path-based dedup so revisiting moves an item to front
// rather than duplicating it. Survives refresh via sessionStorage.
const RECENT_STORAGE_KEY = 'agentlets_nav_recent';
// RC-44 — Capped at 5 (was 7). With 7 items at max-width 200px each,
// the recent-trail wrapped or required horizontal scroll on every
// modest viewport, hurting the "jump back" UX it was supposed to
// support. 5 fits comfortably across desktop widths down to ~1280px
// without scroll while still covering the realistic "what was I
// just on" window for an operator.
export const RECENT_CAP = 5;

// Stable key for a recent entry, used by the scope-access filter below.
const recentKey = (e) => `${(e?.type || '').toLowerCase()}:${e?.id || ''}`;

const NavigationContext = createContext(null);

/**
 * NavigationProvider — Tracks user navigation trail with page-level context.
 *
 * Features:
 *   - Maintains an ordered trail of visited pages (breadcrumb-ready)
 *   - Each trail entry can carry contextual labels (e.g. "Default Tenant → Proactive Threat Hunter")
 *   - Pages register their sub-context via pushContext() for deep breadcrumbs
 *   - Trail survives refresh (sessionStorage) and in-session navigation
 *   - Back-navigation pops the trail automatically
 *   - Clicking a trail entry navigates back and trims forward entries
 */
export function NavigationProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(null);

  // ── Trail: ordered list of { path, label, context[] } ──
  const [trail, setTrail] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // ── Per-page context: map of path → [{ label, key }] for sub-breadcrumbs ──
  const [pageContexts, setPageContexts] = useState(() => {
    try {
      const stored = sessionStorage.getItem(CONTEXT_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });

  // ── Recent entities: precise history of worked-on items (cap RECENT_CAP) ──
  const [recentEntities, setRecentEntities] = useState(() => {
    try {
      const stored = sessionStorage.getItem(RECENT_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      // RC-44 — Re-apply the cap on hydration so existing sessions with
      // 7 items (pre-cap-change) immediately render 5 without waiting
      // for the next recordEntity to truncate. Array.slice is safe on
      // any array length.
      return Array.isArray(parsed) ? parsed.slice(0, RECENT_CAP) : [];
    } catch { return []; }
  });

  // Persist trail
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail)); } catch {/* */}
  }, [trail]);

  /* ── RC-56 — Previous-screen tracking for the "← Back to X" pill.
   *
   * Operator pain point: breadcrumbs show the IA hierarchy, and the
   * recent-entities trail shows the last N detail pages — but neither
   * answers "where did I JUST come from to get to this exact screen".
   * The browser back button works, but operators don't always realise
   * it's wired up for SPA routes, and even then they can't SEE the
   * destination label.
   *
   * Approach: track full location (pathname + search) in a small
   * history list. previousScreen exposes index [1] (most recent OTHER
   * than current). Label is derived first from recentEntities (named
   * detail pages), then from PATH_LABELS (route map above), then a
   * humanised slug fallback. Click handler in DashboardLayoutV2
   * navigates to previousScreen.url.
   *
   * Why a separate locationHistory and not just recentEntities[1]:
   * recentEntities is only updated by detail pages calling
   * recordEntity. List pages (e.g. /workflow-intelligence) never
   * call it, so they wouldn't appear in recents — but the operator
   * does navigate through them and wants to back-button to them.
   * locationHistory tracks EVERY navigation, regardless. */
  const [locationHistory, setLocationHistory] = useState(() => [{
    pathname: location.pathname,
    search: location.search,
    ts: Date.now(),
  }]);

  useEffect(() => {
    // Skip when location hasn't actually changed (some renders re-fire
    // the effect without a real navigation).
    setLocationHistory(prev => {
      const head = prev[0];
      if (head && head.pathname === location.pathname && head.search === location.search) {
        return prev;
      }
      const next = [{ pathname: location.pathname, search: location.search, ts: Date.now() },
                    ...prev];
      // Cap at 10 — generous enough for normal back-stack depth, small
      // enough not to bloat sessionStorage if we ever persist this.
      return next.slice(0, 10);
    });
    // Intentionally only depend on pathname + search.
  }, [location.pathname, location.search]);

  // Persist recent entities
  useEffect(() => {
    try { sessionStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recentEntities)); } catch {/* */}
  }, [recentEntities]);

  // Persist page contexts
  useEffect(() => {
    try { sessionStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(pageContexts)); } catch {/* */}
  }, [pageContexts]);

  // ── Track route changes → build trail ──
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === prevPathRef.current) return;
    prevPathRef.current = currentPath;

    const segment = currentPath.split('/').filter(Boolean)[0] || 'overview';
    const label = PATH_LABELS[segment] || segment;

    setTrail(prev => {
      // Check if we're navigating "back" to an existing trail entry
      const existingIdx = prev.findIndex(e => e.path === currentPath);
      if (existingIdx >= 0) {
        // Trim forward entries (user went back)
        return prev.slice(0, existingIdx + 1);
      }

      // Check if we're revisiting the same page (e.g. /agents → /agents)
      const lastEntry = prev[prev.length - 1];
      if (lastEntry && lastEntry.path === currentPath) {
        return prev;
      }

      // New page — push to trail (cap at 10 entries)
      const entry = { path: currentPath, label, segment, timestamp: Date.now() };
      const next = [...prev, entry];
      return next.length > 10 ? next.slice(-10) : next;
    });
  }, [location.pathname]);

  /**
   * pushContext — Register a sub-level breadcrumb for the current page.
   * Called by pages when user drills into something (e.g. selecting an agent,
   * opening a specific workflow, drilling into a graph node).
   *
   * @param {string} label - Display label (e.g. "Proactive Threat Hunter")
   * @param {string} key - Optional unique key (for dedup). Defaults to label.
   */
  const pushContext = useCallback((label, key) => {
    const currentPath = location.pathname;
    const contextKey = key || label;
    setPageContexts(prev => {
      const existing = prev[currentPath] || [];
      // Don't duplicate
      if (existing.some(c => c.key === contextKey)) return prev;
      return {
        ...prev,
        [currentPath]: [...existing, { label, key: contextKey }],
      };
    });
  }, [location.pathname]);

  /**
   * recordEntity — Record a specific worked-on entity (Agent / Workflow /
   * Skill / Connector / …) in the precise recent-history trail. Detail pages
   * call this once the entity's name is known. Most-recent-first, path-deduped,
   * capped at RECENT_CAP (5 — see RC-44). This is what powers the "jump back
   * to what I was working on" trail in the top bar — independent of the IA
   * breadcrumb.
   *
   * @param {{type:string, id?:string, name:string, path:string}} entity
   */
  const recordEntity = useCallback((entity) => {
    if (!entity || !entity.path || !entity.name) return;
    setRecentEntities(prev => {
      const filtered = prev.filter(e => e.path !== entity.path);
      const next = [{
        type: entity.type || 'Page',
        id: entity.id || null,
        name: entity.name,
        path: entity.path,
        ts: Date.now(),
      }, ...filtered];
      return next.slice(0, RECENT_CAP);
    });
  }, []);

  /**
   * removeRecentEntity — Drop a specific entry from the recent trail, by id
   * and/or path. Used to self-heal the trail when an entry is no longer
   * accessible in the current scope — e.g. the canvas calls this after a 404
   * load (workflow in another tenant, or archived/deleted) so the stale chip
   * disappears instead of silently blanking again on the next click.
   *
   * @param {{id?:string, path?:string}} match
   */
  const removeRecentEntity = useCallback((match) => {
    if (!match || (!match.id && !match.path)) return;
    setRecentEntities(prev => prev.filter(e =>
      !((match.id && e.id === match.id) || (match.path && e.path === match.path))
    ));
  }, []);

  // ── Scope-aware Recent filtering (proper RC — all surfaces) ─────────
  // The Recent trail stores bare {type,id,name,path} refs with no scope or
  // validity context. Render ONLY entries the caller can actually access in
  // their CURRENT scope (not another tenant, not archived/deleted) so no
  // surface (top-bar trail, global search, …) ever shows an orphan chip that
  // wastes a click. The backend is the authority — the FE asks "which of these
  // are accessible?" and hides the rest. Scope switches reload the app, so a
  // re-validation on mount naturally reflects the new scope.
  const recentSig = recentEntities.map(recentKey).join('|');
  // { sig, keys } — keys is a Set of accessible "type:id", or null (fail-open).
  const [recentAccess, setRecentAccess] = useState({ sig: null, keys: null });

  useEffect(() => {
    const withIds = recentEntities.filter((e) => e && e.id && e.type);
    if (withIds.length === 0) { setRecentAccess({ sig: recentSig, keys: new Set() }); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await validateRecentAccess(withIds.map((e) => ({ type: e.type, id: e.id })));
        if (cancelled) return;
        if (res?.ok && Array.isArray(res.data?.accessible)) {
          const keys = new Set(res.data.accessible.map(recentKey));
          setRecentAccess({ sig: recentSig, keys });
        } else {
          setRecentAccess({ sig: recentSig, keys: null }); // fail-open — never hide on a non-ok reply
        }
      } catch {
        if (!cancelled) setRecentAccess({ sig: recentSig, keys: null }); // fail-open on transient error
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [recentSig]); // eslint-disable-line

  // Filter only once validation for the CURRENT entry set has landed; while a
  // fresh set is being validated (or on error) show all, so a transient failure
  // never hides valid work. Non-entity chips (no id/type) are always kept.
  const visibleRecentEntities = useMemo(() => {
    if (recentAccess.sig !== recentSig || recentAccess.keys === null) return recentEntities;
    return recentEntities.filter((e) => (!e || !e.id || !e.type) ? true : recentAccess.keys.has(recentKey(e)));
  }, [recentEntities, recentAccess, recentSig]);

  /**
   * popContext — Remove the last sub-level breadcrumb for the current page.
   */
  const popContext = useCallback(() => {
    const currentPath = location.pathname;
    setPageContexts(prev => {
      const existing = prev[currentPath] || [];
      if (existing.length === 0) return prev;
      return { ...prev, [currentPath]: existing.slice(0, -1) };
    });
  }, [location.pathname]);

  /**
   * replaceContext — Replace all sub-level breadcrumbs for the current page.
   * Useful when the page manages its own drill-down stack.
   *
   * @param {Array<{label, key}>} contexts
   */
  const replaceContext = useCallback((contexts) => {
    const currentPath = location.pathname;
    setPageContexts(prev => ({
      ...prev,
      [currentPath]: contexts || [],
    }));
  }, [location.pathname]);

  /**
   * clearContext — Clear sub-level breadcrumbs for the current page.
   */
  const clearContext = useCallback(() => {
    const currentPath = location.pathname;
    setPageContexts(prev => {
      const next = { ...prev };
      delete next[currentPath];
      return next;
    });
  }, [location.pathname]);

  /**
   * navigateTrail — Navigate to a trail entry (trims forward entries).
   */
  const navigateTrail = useCallback((index) => {
    if (index >= 0 && index < trail.length) {
      const entry = trail[index];
      // Trim forward entries
      setTrail(prev => prev.slice(0, index + 1));
      navigate(entry.path);
    }
  }, [trail, navigate]);

  /**
   * getTrail — Returns the current trail with contexts merged in.
   */
  const getFullTrail = useCallback(() => {
    return trail.map(entry => ({
      ...entry,
      contexts: pageContexts[entry.path] || [],
    }));
  }, [trail, pageContexts]);

  /**
   * clearTrail — Reset the navigation trail (e.g. on logout).
   */
  const clearTrail = useCallback(() => {
    setTrail([]);
    setPageContexts({});
    setRecentEntities([]);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(CONTEXT_STORAGE_KEY);
    sessionStorage.removeItem(RECENT_STORAGE_KEY);
  }, []);

  const currentPath = location.pathname;
  const currentSegment = currentPath.split('/').filter(Boolean)[0] || 'overview';
  const currentLabel = PATH_LABELS[currentSegment] || currentSegment;
  const currentContexts = pageContexts[currentPath] || [];

  /* RC-56 — Compute previousScreen for the "← Back to X" pill.
   *
   * Resolution chain for the label (most specific → least):
   *   1. recentEntities entry with the same path → use entity.name +
   *      entity.type (e.g., "Insider Threat Investigation Agent"
   *      kind=Agent). Best UX — concrete entity name.
   *   2. PATH_LABELS map for the route's first segment (e.g.,
   *      "/workflow-intelligence" → "Workflow Intelligence"). Used
   *      for list pages the operator passed through.
   *   3. Humanise the first path segment as a last-resort fallback
   *      ("audit-trail" → "Audit Trail").
   * Returns null when there's no previous screen yet (first
   * navigation of the session), which makes the pill self-hide. */
  const previousScreen = (() => {
    // Pick the most recent history entry that's NOT the current path AND
    // NOT a strict ancestor of it. Ancestor entries are typically transient
    // index-route stops — e.g. /cloud-operations/advanced gets pushed when
    // user lands there before its <Route index Navigate /> bounces to
    // /cloud-operations/advanced/overview. Clicking "Back to ..." on an
    // ancestor URL just re-bounces and goes nowhere. Skip them.
    const cur = location.pathname;
    const prev = locationHistory.slice(1).find(entry => {
      if (!entry || !entry.pathname) return false;
      if (entry.pathname === cur)                     return false;
      if (cur.startsWith(entry.pathname.replace(/\/?$/, '') + '/')) return false;
      return true;
    });
    if (!prev) return null;
    const url = (prev.pathname || '/') + (prev.search || '');

    // (1) Match against recentEntities — strongest label source.
    const recentMatch = recentEntities.find(e => e.path === url || e.path === prev.pathname);
    if (recentMatch) {
      return { url, label: recentMatch.name, kind: recentMatch.type || 'Page' };
    }

    // (2) Match against the IA route map for the first segment.
    const seg = (prev.pathname || '/').replace(/^\/+/, '').split('/')[0] || 'overview';
    if (PATH_LABELS[seg]) {
      return { url, label: PATH_LABELS[seg], kind: 'Page' };
    }

    // (3) Humanise as fallback.
    const humanised = seg
      .split(/[-_]/)
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || 'Previous';
    return { url, label: humanised, kind: 'Page' };
  })();

  return (
    <NavigationContext.Provider value={{
      trail,
      getFullTrail,
      // Scope-filtered: every surface that renders the Recent trail gets only
      // entries accessible in the caller's current scope (see visibleRecentEntities).
      recentEntities: visibleRecentEntities,
      recordEntity,
      removeRecentEntity,
      pushContext,
      popContext,
      replaceContext,
      clearContext,
      navigateTrail,
      clearTrail,
      currentPath,
      currentSegment,
      currentLabel,
      currentContexts,
      pageContexts,
      // RC-56
      previousScreen,
    }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation must be used within NavigationProvider');
  return ctx;
}
